
import Notification from '@/components/helper/Notification.vue'
import DragDrop from '@/components/input/DragDrop.vue'
import GenerateJigFormFields from '@/components/modals/GenerateJigFormFields.vue'
import GenerateJigLoadingSection from '@/components/modals/GenerateJigLoadingSection.vue'
import GenerateJigPreviewSection from '@/components/modals/GenerateJigPreviewSection.vue'
import SelectLibraryObjectModal from '@/components/modals/SelectLibraryObjectModal.vue'
import GenerateJigService, { GenerateJigProgress } from '@/services/GenerateJigService'
import { uploadLibraryModel } from '@/services/generate-jig/upload-library-model'
import { Tenant } from '@/store/modules/app/types'
import { LibraryEntry } from '@/store/modules/teamlibrary/types'
import { Namespace } from '@/store/types'
import { Timing } from '@/utils/timing'
import IconBin from '@svg/IconBin.svg'
import IconCheckCircleFilled from '@svg/IconCheckCircleFilled.svg'
import IconCloseFilled from '@svg/IconCloseFilled.svg'
import IconFourSquares from '@svg/IconFourSquares.svg'
import IconSpark from '@svg/Icon_Spark.svg'
import { Component, Vue } from 'vue-property-decorator'
import { Getter, State } from 'vuex-class'

declare var consoleError: any

type View = 'form' | 'loading' | 'preview'
type SelectedFile = { file: File; name: string; isValid: boolean }
type DragDropFile = File & { isValid?: boolean }

@Component({
  components: {
    DragDrop,
    GenerateJigFormFields,
    GenerateJigLoadingSection,
    GenerateJigPreviewSection,
    IconBin,
    IconCheckCircleFilled,
    IconCloseFilled,
    IconFourSquares,
    IconSpark,
    Notification,
    SelectLibraryObjectModal,
  },
})
export default class GenerateJig extends Vue {
  @Getter('isJigSparkOptIn', { namespace: Namespace.App })
  public isJigSparkOptIn!: boolean

  @Getter('isGlobalAIOptOut', { namespace: Namespace.App })
  public isGlobalAIOptOut!: boolean

  @Getter('allowedModelFileFormats', { namespace: Namespace.App })
  public allowedModelFileFormats!: string[]

  @State('myTenant', { namespace: Namespace.App })
  public myTenant!: Tenant

  private view: View = 'form'
  private selectedFile: SelectedFile | null = null
  private selectedLibraryEntry: LibraryEntry | null = null
  private isFieldsValid: boolean = false
  private errorMessage: string = ''

  private previewUrl: string = ''
  private generatedJigId: string = ''
  private editUrl: string = ''
  private deeplinkUrl: string = ''
  private traceId: string = ''
  private feedbackUserId: string = ''
  private previewWarningCopy: string = ''

  private generationRunSequence: number = 0
  private activeGenerationRunId: number = 0
  private activeGenerationService: GenerateJigService | null = null
  private canCancelActiveRun: boolean = false

  private get isGenerateJigDisabled(): boolean {
    return !this.isJigSparkOptIn || this.isGlobalAIOptOut
  }

  private get feedbackTenantId(): number {
    return this.myTenant && typeof this.myTenant.ID === 'number' ? this.myTenant.ID : -1
  }

  private get hasAssetSelection(): boolean {
    return !!this.selectedFile || !!this.selectedLibraryEntry
  }

  private get isGenerateEnabled(): boolean {
    return this.hasAssetSelection && this.isFieldsValid && !this.isGenerateJigDisabled
  }

  private beforeDestroy() {
    const service = this.activeGenerationService
    const canCancelActiveRun = this.canCancelActiveRun
    this.clearActiveGenerationRun()

    if (service && canCancelActiveRun) {
      void service.cancel().catch((error: any) => {
        consoleError('Failed to cancel generate jig', error)
      })
    }
  }

  private onFilesSelected(files: DragDropFile[]) {
    if (this.isGenerateJigDisabled) {
      return
    }
    if (!files || files.length === 0) {
      this.selectedFile = null
      return
    }
    const file = files[0]
    if (file.isValid === false) {
      this.selectedFile = null
      return
    }
    this.selectedFile = { file, name: file.name, isValid: true }
    this.selectedLibraryEntry = null
    this.errorMessage = ''
  }

  private removeSelectedFile() {
    if (this.isGenerateJigDisabled) {
      return
    }
    this.selectedFile = null
    this.isFieldsValid = false
    this.errorMessage = ''
    const formFields = this.$refs.formFields as InstanceType<typeof GenerateJigFormFields> | undefined
    if (formFields) {
      formFields.reset()
    }
  }

  private openSelectLibraryModal() {
    if (this.isGenerateJigDisabled) {
      return
    }
    const modal = this.$refs.selectLibraryModal as InstanceType<typeof SelectLibraryObjectModal> | undefined
    if (modal) {
      void modal.show()
    }
  }

  private onLibraryEntrySelected(entry: LibraryEntry) {
    if (this.isGenerateJigDisabled || !entry) {
      return
    }
    this.selectedLibraryEntry = entry
    this.selectedFile = null
    this.errorMessage = ''
  }

  private removeSelectedLibraryEntry() {
    if (this.isGenerateJigDisabled) {
      return
    }
    this.selectedLibraryEntry = null
    this.isFieldsValid = false
    this.errorMessage = ''
    const formFields = this.$refs.formFields as InstanceType<typeof GenerateJigFormFields> | undefined
    if (formFields) {
      formFields.reset()
    }
  }

  private onFormValid(isValid: boolean) {
    this.isFieldsValid = isValid
  }

  private isCurrentRun(runId: number, service: GenerateJigService): boolean {
    return this.activeGenerationRunId === runId && this.activeGenerationService === service
  }

  private clearActiveGenerationRun(runId?: number) {
    if (runId != null && runId > 0 && this.activeGenerationRunId !== runId) {
      return
    }
    this.activeGenerationRunId = 0
    this.activeGenerationService = null
    this.canCancelActiveRun = false
  }

  private updateLoadingProgress(progress: GenerateJigProgress) {
    const loadingSection = this.$refs.loadingSection as InstanceType<typeof GenerateJigLoadingSection> | undefined
    if (loadingSection) {
      loadingSection.updateProgress(progress)
    }
  }

  private getDisplayErrorMessage(error: Error | null | undefined): string {
    if (!error || !error.message) {
      return 'Something went wrong. Please try again.'
    }
    const message = error.message
    const exposesInternalPath =
      message.indexOf('/models/') >= 0 ||
      message.indexOf('ItemUrl') >= 0 ||
      message.indexOf('http://') >= 0 ||
      message.indexOf('https://') >= 0
    if (exposesInternalPath) {
      return 'This model could not be prepared for Jig generation. Please check the file and try again.'
    }
    return message
  }

  private resetToForm() {
    this.view = 'form'
    this.previewUrl = ''
    this.generatedJigId = ''
    this.editUrl = ''
    this.deeplinkUrl = ''
    this.traceId = ''
    this.feedbackUserId = ''
    this.previewWarningCopy = ''
    this.errorMessage = ''
    this.selectedFile = null
    this.selectedLibraryEntry = null
    this.isFieldsValid = false
  }

  private async onGenerateClick() {
    if (!this.isGenerateEnabled) {
      return
    }

    const formFields = this.$refs.formFields as InstanceType<typeof GenerateJigFormFields> | undefined
    const formPayload = formFields ? formFields.getPayload() : null
    if (!formPayload) {
      return
    }

    const tenantId = this.myTenant && typeof this.myTenant.ID === 'number' ? this.myTenant.ID : -1
    if (tenantId <= 0) {
      this.errorMessage = 'Could not resolve tenant ID for this session.'
      return
    }

    this.errorMessage = ''
    this.view = 'loading'
    await this.$nextTick()
    this.updateLoadingProgress({ message: 'Starting...', percentage: 0 })

    // Upload path is skipped when the user picked an existing library object —
    // the entry is already in the team library, so feed it straight to the
    // generation service.
    const isLibraryEntryPath = !!this.selectedLibraryEntry
    let libraryEntry: LibraryEntry
    if (isLibraryEntryPath) {
      libraryEntry = this.selectedLibraryEntry as LibraryEntry
    } else if (this.selectedFile) {
      try {
        libraryEntry = await uploadLibraryModel({
          store: this.$store,
          file: this.selectedFile.file,
          onProgress: (progress: GenerateJigProgress) => this.updateLoadingProgress(progress),
        })
      } catch (error) {
        const err = error as Error
        this.errorMessage = this.getDisplayErrorMessage(err)
        this.view = 'form'
        return
      }
    } else {
      this.view = 'form'
      return
    }

    const service = new GenerateJigService(tenantId)
    const runId = ++this.generationRunSequence
    this.activeGenerationRunId = runId
    this.activeGenerationService = service
    this.canCancelActiveRun = true

    try {
      const result = await service.generateJig(
        {
          libraryEntry,
          userQuestion: formPayload.description,
          jigName: formPayload.name,
          jigUseCase: formPayload.useCaseLabel,
          jigUseCaseTemplateName: formPayload.useCase,
          libraryModelReferences: [],
          tenantName: this.myTenant && this.myTenant.Name ? this.myTenant.Name : '',
        },
        (progress: GenerateJigProgress) => {
          if (!this.isCurrentRun(runId, service)) {
            return
          }
          if (isLibraryEntryPath) {
            // No upload phase — GenerateJigService progress maps directly to 0–100.
            this.updateLoadingProgress(progress)
            return
          }
          // Upload phase covered 0–60. Remap GenerateJigService's 0–100 onto 60–100
          // so the bar grows monotonically across both phases.
          const remapped: GenerateJigProgress = {
            message: progress.message,
            percentage: 60 + Math.round((progress.percentage || 0) * 0.4),
          }
          this.updateLoadingProgress(remapped)
        }
      )

      if (!this.isCurrentRun(runId, service)) {
        return
      }

      service.commitGeneratedJig()
      this.canCancelActiveRun = false

      this.updateLoadingProgress({ message: 'Jig created successfully.', percentage: 100 })
      await Timing.sleep(260)

      if (!this.isCurrentRun(runId, service)) {
        return
      }

      this.generatedJigId = result.jigId
      this.previewUrl = result.previewURL
      this.editUrl = result.editURL
      this.deeplinkUrl = result.deeplinkURL
      this.traceId = result.traceId || ''
      this.feedbackUserId = result.userId || ''
      this.previewWarningCopy = result.warningCopy || ''

      this.view = 'preview'
      this.clearActiveGenerationRun(runId)
    } catch (error) {
      if (!this.isCurrentRun(runId, service)) {
        return
      }
      this.clearActiveGenerationRun(runId)
      const err = error as Error
      this.errorMessage = this.getDisplayErrorMessage(err)
      this.view = 'form'
    }
  }
}
